import { Alert, Modal as ShownModal } from "antd";
import restClient from "api/restClient";
import { Button } from "components/Button/Button";
import { ActionDropdownButtonWithoutParent } from "components/DropdownButton/ActionDropdownButton";
import { requiredValidator } from "components/Form/Form.utils";
import { StoreProviderForm } from "components/Form/StoreProviderForm";
import { AutocompleteFormComponent } from "components/Form/components/Autocomplete/AutocompleteFormComponent";
import { CancelButton } from "components/Form/components/CancelButton/CancelButton";
import { CopyToClipboardFormComponent } from "components/Form/components/CopyToClipboard/CopyToClipboardFormComponent";
import { Form } from "components/Form/components/Form/Form";
import { TextFormComponent } from "components/Form/components/Text/TextFormComponent";
import { IconLike } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import IconButton from "components/IconButton";
import {
    getUncommittedStatusBadgeIcon,
    IconWithStatusBadge,
    UncommittedStatusBadgeImage,
} from "components/IconWithStatusBadge/IconWithStatusBadge";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import Tooltip from "components/Tooltip/Tooltip";
import { FeConfigType } from "config/config";
import { getUserData } from "containers/Auth/Auth.selectors";
import { getDocumentationUrl } from "containers/Documentation/documentationService";
import { getActiveOrDefaultProjectId } from "containers/Projects/Projects.selectors";
import { actions as resourcesActions } from "containers/Resources/Resources.reducer";
import { Action, ActionData } from "containers/Resources/components/CommonActions/Action";
import { createModalActionSettlement } from "containers/Resources/components/CommonActions/actionModalSettlement";
import {
    forceRecordUpdate,
    getReadOnlyOrDeleted,
} from "containers/Resources/components/CommonActions/CommonActions";
import { BulkCommitAction } from "containers/Resources/screens/ResourcesSingle/components/GitAction/commitActions/BulkCommitAction";
import { BulkResetForm } from "containers/Resources/screens/ResourcesSingle/components/GitAction/commitActions/formComponents/BulkResetForm";
import {
    bulkReset,
    getUncommittedChangesCount,
    isContainerType,
    isGitActionButtonDisabled,
    UncommittedDataGroupedByName,
} from "containers/Resources/screens/ResourcesSingle/components/GitAction/commitActions/utils";
import { submitSingleRecordForm } from "containers/Resources/screens/ResourcesSingle/utils";
import { Bundle, Project, Workspace } from "graphql/generated/graphql";
import { useMemo, useState } from "react";
import { Field, Form as FinalForm } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { getTypedLowerCase } from "utils/service";
import style from "./GitAction.module.scss";

interface GitActionProps {
    data: Project | Bundle | Workspace;
    isDisabled?: boolean;
    afterAction: () => void;
    uncommittedData: UncommittedDataGroupedByName;
    fetchSearchTabs?: () => Promise<any> | undefined;
    isGitLocked?: boolean;
}

interface ModalActionProps {
    data: ActionData;
    afterAction: () => void;
    fetchSearchTabs?: () => Promise<any> | undefined;
}

export const getIsGitEnabled = (data: ActionData) => !!data?.resourceData?.branch_name;
export const getRecordOrContainerIsGitEnabled = (resourceType: FeConfigType, resourceData: any) =>
    isContainerType(resourceType)
        ? !!resourceData?.branch_name
        : resourceData?.bundle_id_bundle ?? resourceData?.project_id_project
        ? !!resourceData?.bundle_id_bundle?.branch_name ||
          !!resourceData?.project_id_project?.branch_name
        : !!resourceData?.workspace_id_workspace?.branch_name;

export const getInitialModal = (title?: string) =>
    ShownModal.confirm({
        title,
        className: style.gitActionModal,
        icon: null,
        onOk: () => {},
        onCancel: () => {},
        okText: "",
        cancelText: "",
        okButtonProps: { disabled: true },
        cancelButtonProps: { disabled: true },
        centered: true,
    });

interface GitWarningContainerProps {
    data: ActionData;
    handleClick: () => void;
    closeModal: () => void;
}

interface GitWarningProps {
    message: string;
    additionalText?: string;
    handleClick: () => void;
    closeModal: () => void;
    buttonLabel: string;
    buttonIcon?: IconLike;
}

const GitWarning = ({
    message,
    additionalText,
    closeModal,
    handleClick,
    buttonLabel,
    buttonIcon,
}: GitWarningProps) => {
    const [isDisabled, setDisabled] = useState(false);

    return (
        <div>
            <div className="warningMessage">
                <Alert message={message} type="warning" />
            </div>
            <div style={{ marginTop: "16px" }}>{additionalText ?? null}</div>
            <div className="modalActions">
                <CancelButton onClick={() => closeModal()} />
                <Button
                    onClick={() => {
                        setDisabled(true);
                        handleClick();
                    }}
                    variation="danger"
                    label={buttonLabel}
                    icon={buttonIcon}
                    loading={isDisabled}
                />
            </div>
        </div>
    );
};

const DisableGitContent = ({ data, handleClick, closeModal }: GitWarningContainerProps) => {
    return (
        <GitWarning
            message="Disabling git will delete the git repository and all the files in the repository, including the git history."
            additionalText={`The ${data.resourceType} and all its resources will remain in your Cloudomation workspace but the git repository will be fully deleted. You can re-enable git at any time, which will create a new git repository.`}
            closeModal={closeModal}
            handleClick={handleClick}
            buttonLabel="Disable git & delete git repository"
        />
    );
};

const DisableGitAction: Action = (data, dispatch) => {
    return new Promise((resolve, reject) => {
        const modal = getInitialModal(
            `Disable git for ${data.resourceType} "${data.resourceData.name}"`
        );

        const settlement = createModalActionSettlement(() => modal.destroy(), { resolve, reject });

        const disableGit = () => {
            new Promise((dispatchResolve, dispatchReject) => {
                dispatch({
                    type: resourcesActions.START_UPDATE_RESOURCE,
                    promise: { resolve: dispatchResolve, reject: dispatchReject },
                    resourceName: data.resourceType,
                    variables: {
                        id: data.resourceData?.id,
                        branch_name: null,
                    },
                });
            })
                .then((result) => settlement.succeed(result))
                .catch((error) => settlement.fail(error));
        };

        modal.update((previousConfig) => ({
            ...previousConfig,
            content: (
                <DisableGitContent
                    data={data}
                    handleClick={disableGit}
                    closeModal={settlement.cancel}
                />
            ),
        }));
    });
};

const getRepositoryName = (gitUrl?: string | null) => {
    if (!gitUrl) {
        return "";
    }
    const gitUrlArr = gitUrl.split("/");
    // take repository_name between last "/" and ".git"
    const repositoryName = gitUrlArr[gitUrlArr.length - 1].replace(".git", "");

    return repositoryName;
};
interface RepositoryNameFieldProps {
    resourceId: string;
    resourceType: string;
    gitUrl?: string;
    form: any;
}

const RepositoryNameField = ({
    resourceId,
    resourceType,
    gitUrl,
    form,
}: RepositoryNameFieldProps) => {
    return (
        <Field
            name="repository_name"
            validate={requiredValidator}
            initialValue={getRepositoryName(gitUrl || "")}
        >
            {({ input, meta }) => (
                <TextFormComponent
                    input={{
                        ...input,
                        onBlur: (event: any) => {
                            const submitButton =
                                event?.relatedTarget && event.relatedTarget.type === "submit";
                            if (submitButton) {
                                return;
                            }
                            const repositoryName = event?.target?.value;
                            restClient
                                .get(
                                    `${resourceType.toLowerCase()}/${resourceId}/git/remote_url?repository_name=${encodeURIComponent(
                                        repositoryName
                                    )}`
                                )
                                .then((data: any) => {
                                    const urls = data?.data;
                                    form.mutators.setNewValue(urls?.ssh);
                                    if (form?.mutators?.setCloneValues) {
                                        form.mutators.setCloneValues(urls);
                                    }
                                })
                                .catch((error) => {
                                    NotificationService.error(
                                        <RestErrorNotification
                                            titleOverride="Sorry, update failed"
                                            error={error}
                                        />
                                    );
                                });
                        },
                    }}
                    fieldProps={{
                        label: "Repository name:",
                    }}
                    fieldAttr={{
                        placeholder: "enter repository name",
                        required: true,
                    }}
                    meta={meta}
                />
            )}
        </Field>
    );
};

const ManageGitBranchAction: Action = async (data, dispatch, history) => {
    let submitButtonText = "Switch branch";
    const isGitEnabled = getIsGitEnabled(data);

    let branchList = [];
    if (isGitEnabled) {
        await restClient
            .get(
                `${data.resourceData.resource_type.toLowerCase()}/${
                    data.resourceData.id
                }/git/branch`
            )
            .then((data: any) => {
                branchList = data.data.map((branch: any) => ({
                    label: branch.name,
                    value: branch.name,
                }));
            })
            .catch((error) =>
                NotificationService.error(
                    <RestErrorNotification
                        titleOverride="Failed to fetch branch list"
                        error={error}
                    />
                )
            );
    }

    return new Promise((resolve, reject) => {
        const modal = getInitialModal(
            `${isGitEnabled ? "Manage git branch" : "Create git repository"} for ${
                data.resourceType
            } "${data.resourceData.name}"`
        );

        const settlement = createModalActionSettlement(() => modal.destroy(), { resolve, reject });

        const manageGit = (formData) => {
            submitSingleRecordForm({
                formData: {
                    branch_name: formData.branch_name,
                    repository_name: formData.repository_name,
                    id: data.resourceData.id,
                },
                resourceConfig: {},
                dispatch,
                resourceName: data.resourceType,
            })
                ?.then((result) => {
                    NotificationService.success(
                        isGitEnabled ? "Branch updated" : "Git repository created"
                    );
                    // Refresh the record view so the new git state (branch/git badge)
                    // shows immediately without a manual page reload.
                    forceRecordUpdate(dispatch);
                    data?.afterAction?.();
                    settlement.succeed(result);
                })
                ?.catch((error) => {
                    NotificationService.error(<RestErrorNotification error={error} />);
                    settlement.fail(error);
                });
        };

        const form = (
            <StoreProviderForm>
                <FinalForm
                    onSubmit={manageGit}
                    initialValues={{
                        branch_name: data.resourceData.branch_name,
                        repository_name: getRepositoryName(data.resourceData.git_url),
                    }}
                    mutators={{
                        setNewValue: (args, state, utils) => {
                            const newGitUrl = args[0];
                            utils.changeValue(state, "repository_name", () =>
                                getRepositoryName(newGitUrl)
                            );
                        },
                    }}
                >
                    {({ handleSubmit, pristine, hasValidationErrors, submitSucceeded, form }) => {
                        return (
                            <Form onSubmit={handleSubmit}>
                                <Form.Row>
                                    {isGitEnabled ? null : (
                                        <RepositoryNameField
                                            resourceId={data.resourceData.id}
                                            resourceType={data.resourceType}
                                            gitUrl={data.resourceData.git_url}
                                            form={form}
                                        />
                                    )}
                                </Form.Row>
                                <Form.Row>
                                    {isGitEnabled ? (
                                        <Field
                                            name="branch_name"
                                            component={AutocompleteFormComponent}
                                            fieldProps={{
                                                label: "Branch name:",
                                                isCreatable: true,
                                                applyActionOnCreate: () => {
                                                    submitButtonText = "Create branch";
                                                },
                                                applyActionOnChange: () => {
                                                    submitButtonText = "Switch branch";
                                                },
                                                options: {
                                                    list: branchList,
                                                },
                                            }}
                                            fieldAttr={{ required: true }}
                                            validate={requiredValidator}
                                        />
                                    ) : (
                                        <Field
                                            name="branch_name"
                                            component={TextFormComponent}
                                            fieldProps={{
                                                label: "Branch name:",
                                            }}
                                            fieldAttr={{
                                                placeholder: "enter branch name",
                                                required: true,
                                            }}
                                            validate={requiredValidator}
                                        />
                                    )}
                                </Form.Row>
                                <Form.Row>
                                    <div className="form__row__margin">
                                        <CancelButton onClick={settlement.cancel} />
                                        <Button
                                            submit
                                            disabled={pristine || hasValidationErrors}
                                            loading={submitSucceeded}
                                            variation="primary"
                                            label={
                                                isGitEnabled
                                                    ? submitButtonText
                                                    : "Create git repository"
                                            }
                                        />
                                    </div>
                                </Form.Row>
                            </Form>
                        );
                    }}
                </FinalForm>
            </StoreProviderForm>
        );

        modal.update((previousConfig) => ({
            ...previousConfig,
            content: form,
        }));
    });
};

const ManageGitRepositoryAction: Action = (data, dispatch, history) => {
    return new Promise((resolve, reject) => {
        const modal = getInitialModal(
            `Manage git repository for ${data.resourceType} "${data.resourceData.name}"`
        );

        const settlement = createModalActionSettlement(() => modal.destroy(), { resolve, reject });

        modal.update((previousConfig) => ({
            ...previousConfig,
            content: <div>Loading…</div>,
        }));

        const manageGitRepository = (formData) => {
            submitSingleRecordForm({
                formData: {
                    id: data.resourceData.id,
                    repository_name: formData.repository_name,
                },
                resourceConfig: {},
                dispatch,
                resourceName: data.resourceType,
            })
                ?.then((result) => settlement.succeed(result))
                ?.catch((error) => settlement.fail(error));
        };

        const userId = data?.userData?.identity_id;
        const initialRepoName = getRepositoryName(data.resourceData.git_url);
        const urlsPromise = restClient.get(
            `${data.resourceType.toLowerCase()}/${data.resourceData.id}/git/remote_url?repository_name=${encodeURIComponent(
                initialRepoName
            )}`
        );
        // `has_pat` is a virtual column; ensure it’s explicitly selected.
        const patPromise = userId
            ? restClient.get(`user/${userId}?fields=has_pat`)
            : Promise.resolve(null);

        Promise.allSettled([urlsPromise, patPromise]).then(([urlsRes, patRes]) => {
            const urls =
                urlsRes.status === "fulfilled"
                    ? (urlsRes.value as any)?.data
                    : { ssh: data.resourceData.git_url, https: "" };
            const user =
                patRes && (patRes as any).status === "fulfilled" ? (patRes as any).value?.data : null;
            // REST responses are shaped like `{ user: { ... } }`
            const hasPat = !!user?.user?.has_pat;

            const sshUrl = urls?.ssh ?? data.resourceData.git_url;
            const httpsUrl = urls?.https ?? "";

            const form = (
                <>
                    <StoreProviderForm>
                        <FinalForm
                            onSubmit={manageGitRepository}
                            initialValues={{
                                git_url: sshUrl,
                                repository_name: getRepositoryName(sshUrl),
                                clone_ssh: `git clone ${sshUrl}`,
                                clone_https: httpsUrl ? `git clone ${httpsUrl}` : "",
                            }}
                            mutators={{
                                setNewValue: (args, state, utils) => {
                                    const newGitUrl = args[0];
                                    utils.changeValue(state, "repository_name", () =>
                                        getRepositoryName(newGitUrl)
                                    );
                                    utils.changeValue(state, "git_url", () => newGitUrl);
                                },
                                setCloneValues: (args, state, utils) => {
                                    const next = args[0] || {};
                                    utils.changeValue(state, "clone_ssh", () =>
                                        next.ssh ? `git clone ${next.ssh}` : ""
                                    );
                                    utils.changeValue(state, "clone_https", () =>
                                        next.https ? `git clone ${next.https}` : ""
                                    );
                                },
                            }}
                        >
                            {({
                                handleSubmit,
                                pristine,
                                hasValidationErrors,
                                form,
                                submitSucceeded,
                            }) => {
                                const isRepositoryNameChanged =
                                    form.getState().dirtyFields.repository_name;
                                return (
                                    <Form onSubmit={handleSubmit}>
                                        {isRepositoryNameChanged && (
                                            <div style={{ marginBottom: "16px" }}>
                                                <Alert
                                                    message={`Warning: Existing local git clones of this repository will need to
                                be reconfigured to point to the new repository name.`}
                                                    description={
                                                        <div>
                                                            Please consult the{" "}
                                                            <a
                                                                href={getDocumentationUrl(
                                                                    "/docs/git-integration#re-synchronising-the-remote-and-local-repository-needed-after-renaming-the-remote-repository"
                                                                )}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                documentation
                                                            </a>{" "}
                                                            for more information.
                                                        </div>
                                                    }
                                                    type="warning"
                                                    showIcon
                                                />
                                            </div>
                                        )}

                                        <Form.Row>
                                            <RepositoryNameField
                                                resourceId={data.resourceData.id}
                                                resourceType={data.resourceType}
                                                gitUrl={sshUrl}
                                                form={form}
                                            />
                                        </Form.Row>

                                        <Form.Row>
                                            <Field
                                                name="git_url"
                                                component={CopyToClipboardFormComponent}
                                                fieldProps={{
                                                    label: "Git URL (SSH):",
                                                }}
                                            />
                                        </Form.Row>

                                        <Form.Row>
                                            <Field
                                                name="clone_ssh"
                                                component={CopyToClipboardFormComponent}
                                                fieldProps={{
                                                    label: "Clone via SSH:",
                                                }}
                                            />
                                        </Form.Row>

                                        <Form.Row>
                                            <Field
                                                name="clone_https"
                                                component={CopyToClipboardFormComponent}
                                                fieldProps={{
                                                    label: "Clone via HTTPS:",
                                                }}
                                            />
                                        </Form.Row>

                                        <div style={{ marginTop: 8, marginBottom: 12 }}>
                                            <Alert
                                                type={hasPat ? "info" : "warning"}
                                                showIcon
                                                message={
                                                    hasPat
                                                        ? "HTTPS cloning requires your Personal Access Token (PAT)."
                                                        : "HTTPS cloning requires a Personal Access Token (PAT). You don’t have one yet."
                                                }
                                                description={
                                                    <div>
                                                        Create one on your user page under <b>Git HTTPS token</b>. Use it as the
                                                        password when Git prompts for credentials.
                                                    </div>
                                                }
                                            />
                                        </div>

                                        <Form.Row>
                                            <CancelButton onClick={settlement.cancel} />
                                            <Button
                                                submit
                                                disabled={pristine || hasValidationErrors}
                                                loading={submitSucceeded}
                                                variation="primary"
                                                label={"Save"}
                                            />
                                        </Form.Row>
                                    </Form>
                                );
                            }}
                        </FinalForm>
                    </StoreProviderForm>
                </>
            );

            modal.update((previousConfig) => ({
                ...previousConfig,
                content: form,
            }));
        });

    });
};

const ResetToStatusInGitContent = ({ data, handleClick, closeModal }: GitWarningContainerProps) => {
    return (
        <GitWarning
            message={`Any changes you made will be lost and the ${data.resourceType} "${data.resourceData.name}" will be reset to the last commit in git.`}
            closeModal={closeModal}
            handleClick={handleClick}
            buttonLabel="Reset to status in git"
            buttonIcon={IconCollection.undo}
        />
    );
};

export const ResetToStatusInGitAction: Action = async (data, dispatch) => {
    const locationIdPath = `${data.resourceType}/${data.resourceData.id}`;

    return new Promise((resolve, reject) => {
        const modal = getInitialModal(
            `Reset to status in git for ${data.resourceType} "${data.resourceData.name}"`
        );

        const settlement = createModalActionSettlement(() => modal.destroy(), { resolve, reject });

        const resetToStatusInGit = () => {
            restClient
                .patch(`${locationIdPath}/git/restore`)
                .then(() => forceRecordUpdate(dispatch))
                .then((result) => settlement.succeed(result))
                .catch((error) => {
                    NotificationService.error(<RestErrorNotification error={error} />);
                    settlement.fail(error);
                });
        };

        modal.update((previousConfig) => ({
            ...previousConfig,
            content: (
                <ResetToStatusInGitContent
                    data={data}
                    handleClick={resetToStatusInGit}
                    closeModal={settlement.cancel}
                />
            ),
        }));
    });
};
export const BulkResetToStatusInGitAction: Action = async (data, dispatch) => {
    const uncommittedData = data?.uncommittedData;
    const uncommittedRecordList = Object.values(uncommittedData ?? {});

    return new Promise((resolve, reject) => {
        const location = data.resourceType;
        const locationName = data.resourceData.name;
        const modal = getInitialModal(
            `Bulk reset to status in git for ${location} "${locationName}"`
        );

        const settlement = createModalActionSettlement(() => modal.destroy(), { resolve, reject });

        const form = (
            <StoreProviderForm>
                <FinalForm
                    onSubmit={(formData: any) =>
                        bulkReset({
                            formData,
                            actionData: data,
                            dispatch,
                        })
                            .then((result) => settlement.succeed(result))
                            .catch((error) => settlement.fail(error))
                    }
                    initialValues={{
                        records: [],
                    }}
                >
                    {(props: any) => (
                        <BulkResetForm
                            uncommittedRecordList={uncommittedRecordList}
                            closeModal={settlement.cancel}
                            {...props}
                        />
                    )}
                </FinalForm>
            </StoreProviderForm>
        );

        modal.update((previousConfig) => ({
            ...previousConfig,
            content: form,
        }));
    });
};

const ManageGitActionList = ({ data, afterAction, fetchSearchTabs }: ModalActionProps) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const uncommittedChangesCount = getUncommittedChangesCount(data?.uncommittedData);

    return (
        <>
            <div className={style.manageGitActions}>
                <Button
                    key={"bulkCommit"}
                    label={"Bulk commit"}
                    icon={getUncommittedStatusBadgeIcon(uncommittedChangesCount)}
                    image={
                        uncommittedChangesCount ? (
                            <UncommittedStatusBadgeImage
                                uncommittedChangesCount={uncommittedChangesCount}
                            />
                        ) : null
                    }
                    disabled={isGitActionButtonDisabled(data)}
                    variation="clear"
                    className="wide"
                    onClick={() => BulkCommitAction({ ...data, afterAction }, dispatch, history)}
                />
                <Button
                    key={"manageGit"}
                    label={"Manage git branch"}
                    icon={IconCollection.git}
                    disabled={getReadOnlyOrDeleted(data)}
                    variation="clear"
                    className="wide"
                    onClick={() =>
                        ManageGitBranchAction(
                            {
                                ...data,
                                afterAction: () => {
                                    fetchSearchTabs?.();
                                },
                            },
                            dispatch,
                            history
                        )
                    }
                />
                <Button
                    key={"manage_git_repository"}
                    label={"Manage git repository"}
                    icon={IconCollection.git}
                    disabled={getReadOnlyOrDeleted(data)}
                    variation="clear"
                    className="wide"
                    onClick={() => ManageGitRepositoryAction(data, dispatch, history)}
                />
                <Button
                    key={"bulkResetToStatusInGit"}
                    label={"Bulk reset to status in git"}
                    icon={IconCollection.undo}
                    disabled={isGitActionButtonDisabled(data)}
                    variation="clear"
                    className="wide"
                    onClick={() =>
                        BulkResetToStatusInGitAction({ ...data, afterAction }, dispatch, history)
                    }
                />
                <Button
                    key={"disableGit"}
                    label={"Disable git"}
                    icon={IconCollection.git}
                    disabled={getReadOnlyOrDeleted(data)}
                    variation="clear"
                    className="wide"
                    onClick={() => DisableGitAction(data, dispatch, history)}
                />
            </div>
        </>
    );
};

export const GitAction = ({
    data,
    isDisabled,
    afterAction,
    uncommittedData,
    fetchSearchTabs,
    isGitLocked,
}: GitActionProps) => {
    const uncommittedChangesCount = getUncommittedChangesCount(uncommittedData);

    const dispatch = useDispatch();
    const history = useHistory();

    const userData = useSelector(getUserData);

    const activeProjectId = useSelector(getActiveOrDefaultProjectId);

    const isGitEnabled = useMemo(() => data.branch_name, [data.branch_name]);

    const tooltipOverlay = isGitLocked
        ? "Another git operation in progress"
        : uncommittedChangesCount
        ? `${uncommittedChangesCount} uncommitted change${uncommittedChangesCount > 1 ? "s" : ""}`
        : isGitEnabled
        ? "git enabled"
        : "enable git";

    const actionData = useMemo(
        () => ({
            resourceData: data,
            resourceType: getTypedLowerCase(data.resource_type ?? ""),
            userData,
            activeProjectId,
            isReadOnly: isDisabled ?? false,
            uncommittedData,
        }),
        [activeProjectId, data, isDisabled, uncommittedData, userData]
    );

    const statusBadgeProps = uncommittedChangesCount
        ? { type: "primary-red", text: String(uncommittedChangesCount), size: "smallText" }
        : { type: "primary-green", size: "small", statusBadgeStyle: { top: "-2px" } };

    return (
        <div>
            {isGitEnabled ? (
                <>
                    <ActionDropdownButtonWithoutParent
                        dropdownIcon={IconCollection.git}
                        isDisabled={isDisabled || isGitLocked}
                        key={"gitAction"}
                        trigger={["click"]}
                        className="dropdownActionIcon"
                        tooltipOverlay={tooltipOverlay}
                        image={
                            <IconWithStatusBadge
                                icon={IconCollection.git}
                                statusBadgeProps={statusBadgeProps}
                            />
                        }
                    >
                        <ManageGitActionList
                            data={actionData}
                            afterAction={afterAction}
                            fetchSearchTabs={fetchSearchTabs}
                        />
                    </ActionDropdownButtonWithoutParent>
                </>
            ) : (
                <Tooltip overlay={tooltipOverlay}>
                    <IconButton
                        onClick={() => ManageGitBranchAction(actionData, dispatch, history)}
                        icon={IconCollection.git}
                        disabled={isDisabled}
                    />
                </Tooltip>
            )}
        </div>
    );
};
